import { useEffect, useRef, useState } from "react";
import { Building2, Check, Loader2, X } from "lucide-react";
import { Button } from "../ui/button";

export type OrgOption = { id: string; name: string };

type OrgComboboxProps = {
  value: string | undefined;
  onChange: (organizationId: string | undefined) => void;
};

export const OrgCombobox = ({ value, onChange }: OrgComboboxProps) => {
  const [orgs, setOrgs] = useState<OrgOption[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [orgLoadState, setOrgLoadState] = useState<"loading" | "ready" | "error">("loading");
  const [loadAttempt, setLoadAttempt] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const previousValueRef = useRef(value);
  const inputChangeRef = useRef(false);

  // Load orgs once on mount
  useEffect(() => {
    const controller = new AbortController();
    setOrgLoadState("loading");
    fetch("/api/internal/admin/organizations", {
      credentials: "include",
      signal: controller.signal,
    })
      .then((r) => {
        if (!r.ok) throw new Error(`Failed to fetch organizations (${r.status})`);
        return r.json();
      })
      .then((data: unknown) => {
        if (!Array.isArray(data)) throw new Error("Invalid organizations response");
        setOrgs(data as OrgOption[]);
        setOrgLoadState("ready");
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setOrgs([]);
        setOrgLoadState("error");
      });
    return () => controller.abort();
  }, [loadAttempt]);

  // When value changes externally (e.g. editing a different user), sync display label
  useEffect(() => {
    const valueChanged = previousValueRef.current !== value;

    if (!value) {
      if (valueChanged && !inputChangeRef.current) setInputValue("");
      inputChangeRef.current = false;
      previousValueRef.current = value;
      return;
    }
    const match = orgs.find((o) => o.id === value);
    if (match) setInputValue(match.name);
    inputChangeRef.current = false;
    previousValueRef.current = value;
  }, [value, orgs]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const trimmed = inputValue.trim();
  const filtered = trimmed
    ? orgs.filter((o) => o.name.toLowerCase().includes(trimmed.toLowerCase()))
    : orgs;

  const exactMatch = orgs.some(
    (o) => o.name.toLowerCase() === trimmed.toLowerCase(),
  );
  const showCreate =
    (orgLoadState === "ready" || orgLoadState === "error") &&
    trimmed.length > 0 &&
    !exactMatch;

  const selectOrg = (org: OrgOption) => {
    setInputValue(org.name);
    onChange(org.id);
    setIsOpen(false);
  };

  const handleCreate = async () => {
    if (!trimmed || isCreating) return;
    setIsCreating(true);
    try {
      const res = await fetch("/api/internal/admin/organizations", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: trimmed }),
      });
      if (!res.ok) throw new Error("Failed to create org");
      const newOrg: OrgOption = await res.json();
      setOrgs((prev) =>
        [...prev, newOrg].sort((a, b) => a.name.localeCompare(b.name)),
      );
      selectOrg(newOrg);
    } catch {
      // leave input as-is so user can retry
    } finally {
      setIsCreating(false);
    }
  };

  const handleClear = () => {
    setInputValue("");
    onChange(undefined);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="flex h-9 items-center gap-2 rounded-md border border-input bg-transparent px-3 shadow-xs transition-[color,box-shadow] focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]">
        <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
        <input
          type="text"
          value={inputValue}
          placeholder="Search organizations or type to create..."
          onFocus={() => setIsOpen(true)}
          onChange={(e) => {
            setInputValue(e.target.value);
            inputChangeRef.current = true;
            onChange(undefined);
            setIsOpen(true);
          }}
          autoComplete="off"
          className="h-full w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
        />
        {inputValue && (
          <button
            type="button"
            aria-label="Clear organization"
            onClick={handleClear}
            className="shrink-0 rounded-md p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {isOpen &&
        (orgLoadState !== "ready" || filtered.length > 0 || showCreate) && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-border/60 bg-popover shadow-md">
          <ul className="max-h-48 overflow-y-auto py-1 text-sm">
            {orgLoadState === "loading" && (
              <li className="px-3 py-2 text-muted-foreground">
                Loading organizations...
              </li>
            )}
            {orgLoadState === "error" && (
              <li className="flex items-center justify-between gap-3 px-3 py-2 text-destructive">
                <span>Unable to load organizations.</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setLoadAttempt((attempt) => attempt + 1)}
                >
                  Retry
                </Button>
              </li>
            )}
            {filtered.map((org) => (
              <li
                key={org.id}
                className={`flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-muted/60 ${value === org.id ? "bg-muted/40 font-medium" : ""}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectOrg(org);
                }}
              >
                <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="flex-1 truncate">{org.name}</span>
                {value === org.id ? (
                  <Check className="h-4 w-4 shrink-0 text-primary" />
                ) : null}
              </li>
            ))}
            {showCreate && (
              <li
                className="cursor-pointer border-t border-border/40 px-3 py-2 text-muted-foreground hover:bg-muted/60"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleCreate();
                }}
              >
                {isCreating ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Creating...
                  </span>
                ) : (
                  <span>
                    Create new organization: <strong>{trimmed}</strong>
                  </span>
                )}
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};
